// HSSNA 安全存储封装 (Web: AES-GCM + localStorage / RN: 注入适配器)
// 规格: NF-SEC-01 / H-SEC-09 / C-SEC-05 / PIPL §28 / §7 安全合规
//
// 职责:
//   - Web 端: 使用 SubtleCrypto.PBKDF2 派生密钥 + AES-GCM 256 加密
//     存储到 localStorage, 防止直接读取 localStorage 明文 (如 XSS 读取 token)
//   - RN 端: 接口预留, 调用方注入 AsyncStorage + Keychain 适配器
//
// 安全说明:
//   - 密码来源: navigator.userAgent + location.host + 固定 salt
//   - 这并非完美方案 (XSS 仍可调用 SubtleCrypto 解密),
//     但能防止 localStorage 明文落盘被第三方脚本/扩展直接读取
//   - 完美方案应使用 WebAuthn / IndexedDB + Hardware-backed key (后续增强)
//
// 存储 envelope 格式 (JSON):
//   { "v": 1, "iv": "<base64>", "ct": "<base64>" }
//
// 兼容性:
//   - SecureStorageAdapter 结构上兼容 auth/StorageAdapter (可注入 TokenStore)
//   - SSR 安全: typeof window 检查, 非浏览器环境降级为内存适配器 (no-op)

/**
 * 安全存储适配器接口
 *
 * 与 auth/StorageAdapter 结构兼容 (Promise 返回值是 StorageAdapter 联合类型的子集),
 * 可直接传入 TokenStore 构造函数.
 */
export interface SecureStorageAdapter {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

/** 加密 envelope 版本 */
const ENVELOPE_VERSION = 1;

/** PBKDF2 迭代次数 (OWASP 2023 推荐: >= 600,000 for SHA-256; 此处折中 100,000 兼顾性能) */
const PBKDF2_ITERATIONS = 100_000;

/** AES-GCM 密钥长度 (bits) */
const AES_KEY_LENGTH = 256;

/** IV 长度 (bytes, AES-GCM 推荐 12) */
const IV_LENGTH = 12;

/** 固定 salt (与 passphrase 共同派生密钥; 不需要保密, 但需要保持一致) */
const SALT_BYTES = new Uint8Array([
  0x68, 0x73, 0x73, 0x6e, 0x61, 0x2d, 0x73, 0x65, 0x63, 0x72, 0x65, 0x74, 0x2d, 0x76, 0x31, 0x00,
]);

/** envelope JSON 前缀 (用于快速检测加密格式) */
const ENVELOPE_PREFIX = '{"v":';

/**
 * 获取 passphrase (Web 端: userAgent + host)
 * - SSR / 非浏览器环境: 返回固定字符串 (仅用于派生, 不影响 SSR 安全 — SSR 不应读取敏感数据)
 */
function getPassphrase(): string {
  if (typeof window === 'undefined') {
    return 'hssna-ssr-fallback';
  }
  const ua = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';
  const host = typeof window.location !== 'undefined' && window.location.host ? window.location.host : '';
  return `${ua}@${host}`;
}

/** 是否有可用的 SubtleCrypto */
function hasSubtleCrypto(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.crypto !== 'undefined' &&
    typeof window.crypto.subtle !== 'undefined' &&
    typeof window.crypto.subtle.deriveKey === 'function' &&
    typeof window.crypto.subtle.encrypt === 'function'
  );
}

/** 是否有可用的 localStorage */
function hasLocalStorage(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.localStorage !== 'undefined'
  );
}

/** Uint8Array → base64 字符串 */
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i];
    if (byte === undefined) continue;
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

/** base64 字符串 → Uint8Array */
function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** 字符串 → Uint8Array (UTF-8) */
function stringToBytes(input: string): Uint8Array {
  return new TextEncoder().encode(input);
}

/** Uint8Array → 字符串 (UTF-8) */
function bytesToString(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}

/** CryptoKey 缓存 (派生一次后复用, 避免重复 PBKDF2 计算) */
let cachedKey: CryptoKey | null = null;
let cachedPassphrase = '';

/** 派生 AES-GCM 密钥 (PBKDF2 + passphrase + salt) */
async function deriveKey(): Promise<CryptoKey> {
  const passphrase = getPassphrase();

  // 缓存命中 (passphrase 未变)
  if (cachedKey && cachedPassphrase === passphrase) {
    return cachedKey;
  }

  if (!hasSubtleCrypto()) {
    throw new Error('[HSSNA] SubtleCrypto 不可用, 无法派生密钥');
  }

  const subtle = window.crypto.subtle;
  const keyMaterial = await subtle.importKey(
    'raw',
    stringToBytes(passphrase),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  const key = await subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: SALT_BYTES,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: AES_KEY_LENGTH },
    false,
    ['encrypt', 'decrypt'],
  );

  cachedKey = key;
  cachedPassphrase = passphrase;
  return key;
}

/** 加密明文 → envelope JSON 字符串 */
async function encryptValue(plaintext: string): Promise<string> {
  const key = await deriveKey();
  const iv = window.crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const ciphertext = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    stringToBytes(plaintext),
  );

  const ctBytes = new Uint8Array(ciphertext);
  const envelope = {
    v: ENVELOPE_VERSION,
    iv: bytesToBase64(iv),
    ct: bytesToBase64(ctBytes),
  };
  return JSON.stringify(envelope);
}

/** 解密 envelope JSON 字符串 → 明文; 失败返回 null */
async function decryptValue(stored: string): Promise<string | null> {
  // 快速检测 envelope 格式 (避免对非加密值尝试 JSON.parse)
  if (!stored.startsWith(ENVELOPE_PREFIX)) {
    return null;
  }

  let envelope: { v?: number; iv?: string; ct?: string };
  try {
    envelope = JSON.parse(stored) as { v?: number; iv?: string; ct?: string };
  } catch {
    return null;
  }

  if (
    envelope.v !== ENVELOPE_VERSION ||
    typeof envelope.iv !== 'string' ||
    typeof envelope.ct !== 'string'
  ) {
    return null;
  }

  try {
    const key = await deriveKey();
    const iv = base64ToBytes(envelope.iv);
    const ct = base64ToBytes(envelope.ct);
    const plaintext = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      ct,
    );
    return bytesToString(new Uint8Array(plaintext));
  } catch {
    // 解密失败 (密钥变更 / 数据损坏) → 返回 null
    return null;
  }
}

/**
 * 创建 Web 端安全存储 (AES-GCM 加密 + localStorage)
 *
 * - SSR / 非浏览器环境: 降级为内存适配器 (no-op, getItem 返回 null)
 * - SubtleCrypto 不可用: 降级为内存适配器 (并 console.warn)
 */
function createWebSecureStorage(): SecureStorageAdapter {
  // SSR / 非浏览器: 内存适配器 (no-op)
  if (typeof window === 'undefined') {
    return createMemoryAdapter();
  }

  // SubtleCrypto 不可用: 降级 (旧浏览器 / 不安全上下文 http:)
  if (!hasSubtleCrypto()) {
    // eslint-disable-next-line no-console
    console.warn('[HSSNA] SubtleCrypto 不可用, secureStorage 降级为内存适配器 (token 不持久化)');
    return createMemoryAdapter();
  }

  // localStorage 不可用: 降级
  if (!hasLocalStorage()) {
    // eslint-disable-next-line no-console
    console.warn('[HSSNA] localStorage 不可用, secureStorage 降级为内存适配器');
    return createMemoryAdapter();
  }

  return {
    async getItem(key: string): Promise<string | null> {
      const stored = window.localStorage.getItem(key);
      if (stored === null) return null;
      return decryptValue(stored);
    },
    async setItem(key: string, value: string): Promise<void> {
      const encrypted = await encryptValue(value);
      window.localStorage.setItem(key, encrypted);
    },
    async removeItem(key: string): Promise<void> {
      window.localStorage.removeItem(key);
    },
    async clear(): Promise<void> {
      window.localStorage.clear();
    },
  };
}

/**
 * 创建内存适配器 (无持久化, 用于 SSR / 测试 / 降级场景)
 */
export function createMemoryAdapter(): SecureStorageAdapter {
  const store = new Map<string, string>();
  return {
    async getItem(key: string): Promise<string | null> {
      return store.get(key) ?? null;
    },
    async setItem(key: string, value: string): Promise<void> {
      store.set(key, value);
    },
    async removeItem(key: string): Promise<void> {
      store.delete(key);
    },
    async clear(): Promise<void> {
      store.clear();
    },
  };
}

/**
 * 创建安全存储实例
 *
 * @param adapter 自定义底层适配器 (RN 端注入 AsyncStorage + Keychain)
 *                - 未传入时: Web 端使用默认 AES-GCM + localStorage
 *
 * @example
 * ```ts
 * // Web 端 (默认)
 * const storage = createSecureStorage();
 * await storage.setItem('token', 'secret');
 * const token = await storage.getItem('token'); // 'secret'
 *
 * // RN 端 (注入 AsyncStorage + Keychain)
 * const rnStorage = createSecureStorage({
 *   getItem: async (k) => await SecureKeychain.getItem(k),
 *   setItem: async (k, v) => { await SecureKeychain.setItem(k, v); },
 *   removeItem: async (k) => { await SecureKeychain.removeItem(k); },
 *   clear: async () => { await SecureKeychain.clearAll(); },
 * });
 * ```
 */
export function createSecureStorage(adapter?: SecureStorageAdapter): SecureStorageAdapter {
  return adapter ?? createWebSecureStorage();
}

/**
 * 默认安全存储实例
 *
 * - Web 端: AES-GCM 加密 + localStorage
 * - RN 端 / SSR: 调用方需通过 createSecureStorage(adapter) 注入平台适配器
 */
export const secureStorage: SecureStorageAdapter = createSecureStorage();
